import React, { useEffect, useRef, useState } from 'react';

import { ApiObject, Course, IntakeDate, Program, School, StudyOption, Subject } from 'app/types';
import Text from 'common/components/Text/Text';

import styles from 'features/explore/program/tabs/Tabs.module.scss';
import { useMediaQuery } from 'react-responsive';
import { formatMonth, formatNumber } from 'common/utils/conversions';
import { AttributeComponent } from '../../common/components/Overview';
import { capitalizeFirstLetter } from 'common/utils';

interface CompProps {
  program: ApiObject<Program> | undefined;
  programProfileRef: React.RefObject<HTMLDivElement>;
  comesFromExplore?: boolean;
}

interface StudyOptionCardProps {
  studyOption: StudyOption;
  index: number;
}

const StudyOptionCard = ({ studyOption, index }: StudyOptionCardProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  return (
    <div
      className={`card h-100 ${styles.studyOptionContainer}`}
      style={isMobile ? { zIndex: '4' } : { zIndex: 'auto' }}
    >
      <div className={styles.cardHeader}>{`Option ${index + 1}`}</div>
      <div className={styles.cardSection}>
        <div className={styles.attributeBox}>Duration</div>
        <div className={styles.valueBox}>{studyOption.duration_in_months} months</div>
      </div>
      <div className={styles.cardSection}>
        <div className={styles.attributeBox}>Intake time</div>
        <div className={styles.valueBox}>
          {[
            ...new Set(
              studyOption.intake_dates?.map((intake: IntakeDate) => formatMonth(intake?.date))
            ),
          ].join(' / ')}
        </div>
      </div>
      <div className={styles.cardSection}>
        <div className={styles.attributeBox}>Attendance</div>
        <div className={styles.valueBox}>
          {studyOption.delivery === 'In person'
            ? `Campus (${studyOption.city.name})`
            : studyOption.delivery}
        </div>
      </div>
      <div className={styles.cardSection}>
        <div className={styles.attributeBox}>Tuition fee</div>
        <div className={styles.valueBox}>{`${studyOption.tuition_fee.currency} ${formatNumber(
          studyOption.tuition_fee.amount_international
        )}`}</div>
      </div>
    </div>
  );
};

export const ProgramProfileSection = ({
  program,
  programProfileRef,
}: CompProps): React.ReactElement => {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [filteredStudyOptions, setFilteredStudyOptions] = useState<StudyOption[] | undefined>([]);
  const [activeSchedule, setActiveSchedule] = useState<number>(
    (program?.data.study_options.filter(
      (studyOption: StudyOption) => studyOption.schedule === 'full_time'
    )?.length || [].length) > 0
      ? 0
      : 1
  );
  const refScroll = useRef<any>();
  const [availableOptions, setAvailableOptions] = useState<string[]>([]);

  const scroll = (scrollOffset: number) => {
    refScroll.current ? (refScroll.current.scrollLeft += scrollOffset) : '';
  };

  const buildPeriods = (courses?: Course[]) => {
    return [...new Set(courses?.map((course: Course) => course.period_number))];
  };

  useEffect(() => {
    const existsFullTime =
      (program?.data.study_options.filter(
        (studyOption: StudyOption) => studyOption.schedule === 'full_time'
      )?.length || [].length) > 0;
    if (existsFullTime) {
      setFilteredStudyOptions(
        program?.data.study_options.filter((studyOption: StudyOption) =>
          activeSchedule === 0
            ? studyOption.schedule === 'full_time'
            : studyOption.schedule === 'part_time'
        )
      );
    } else {
      setFilteredStudyOptions(
        program?.data.study_options.filter(
          (studyOption: StudyOption) => studyOption.schedule === 'part_time'
        )
      );
    }
  }, [activeSchedule, program]);

  useEffect(() => {
    const fullTimes =
      program?.data.study_options.filter((s: StudyOption) => s.schedule === 'full_time') || [];
    const partTimes =
      program?.data.study_options.filter((s: StudyOption) => s.schedule === 'part_time') || [];
    const array = [];
    if (fullTimes?.length > 0) array.push('Full-Time');
    if (partTimes?.length > 0) array.push('Part-Time');
    setAvailableOptions(array);
  }, [program]);

  return (
    <div
      className={styles.sectionContainer}
      id="program-profile"
      ref={programProfileRef}
      style={{ marginTop: !isMobile ? '80px' : '32px' }}
    >
      <div className={styles.sectionTitle}>
        <div className={styles.blueBlock} />
        {isMobile ? (
          <h2 className={styles.titleText}>Study</h2>
        ) : (
          <h2 className={styles.titleText}>Program profile</h2>
        )}
      </div>
      <h3 className={styles.subtitle}>Study options</h3>
      <div className={styles.sectionContent}>
        <div className={styles.schedulesHeader}>
          <div className={`${styles.schedulesContainer} row flex-row flex-nowrap pr-2`}>
            {availableOptions.map((option, i) => {
              return (
                <div
                  className={`${styles.subject} ${
                    activeSchedule === i ? styles.activeSubject : ''
                  }`}
                  onClick={() => {
                    setActiveSchedule(i);
                  }}
                  key={option}
                >
                  {option}
                </div>
              );
            })}
          </div>
          {isMobile && (
            <div className={styles.arrows}>
              <div className={styles.arrowContainer} onClick={() => scroll(-150)}>
                <i className="bi bi-chevron-compact-left"></i>
              </div>
              <div className={styles.arrowContainer} onClick={() => scroll(150)}>
                <i className="bi bi-chevron-compact-right"></i>
              </div>
            </div>
          )}
        </div>
        {isMobile ? (
          <div
            className={`${styles.studyOptionsContainer} row flex-row flex-nowrap mt-3 pt-1`}
            ref={refScroll}
          >
            {filteredStudyOptions?.map((studyOption: StudyOption, index) => (
              <div className={styles.studyOption} key={studyOption.id}>
                <StudyOptionCard studyOption={studyOption} index={index} />
              </div>
            ))}
          </div>
        ) : (
          !!(filteredStudyOptions?.length && filteredStudyOptions?.length > 0) && (
            <table className="table table-bordered align-middle">
              <thead className="table-light">
                <tr>
                  <th scope="col">Duration</th>
                  <th scope="col">Intake time</th>
                  <th scope="col">Attendance</th>
                  <th scope="col">Tuition</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudyOptions?.map((studyOption: StudyOption) => (
                  <tr key={studyOption.id}>
                    <td>{studyOption.duration_in_months} months</td>
                    <td>
                      {[
                        ...new Set(
                          studyOption.intake_dates?.map((intake: IntakeDate) =>
                            formatMonth(intake?.date)
                          )
                        ),
                      ].join(' / ')}
                    </td>
                    <td>
                      {studyOption.delivery === 'In person'
                        ? `Campus (${studyOption.city.name})`
                        : studyOption.delivery}
                    </td>
                    <td>{`${studyOption.tuition_fee.currency} ${formatNumber(
                      studyOption.tuition_fee.amount_international
                    )}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      {!!(program?.data.courses.length && program?.data.courses.length > 0) && (
        <>
          <h3 className={styles.subtitle} style={{ marginBottom: isMobile ? '16px' : '0px' }}>
            Courses
          </h3>
          <div className={styles.sectionContent}>
            {buildPeriods(program?.data.courses)
              .filter((period) => period !== undefined)
              .map((period: number) => (
                <table className="table table-bordered align-middle mb-0" key={period}>
                  <thead>
                    <tr>
                      <th colSpan={12} className="align-middle" style={{ textAlign: 'initial' }}>
                        {`${period} ${
                          program?.data.period_type
                            ? capitalizeFirstLetter(program?.data.period_type)
                            : 'period'
                        }`}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {program?.data.courses
                      .filter((course) => course.period_number === period)
                      .map((course: Course, index) => (
                        <tr key={course.id}>
                          <td className="w-10" style={{ height: isMobile ? '80px' : '44px' }}>
                            {index + 1}
                          </td>
                          <td>{course.name}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              ))}
          </div>
        </>
      )}
      {(program?.data.assessment_text || program?.data.school.assessment_text) && (
        <div className={styles.sectionContent} style={{ marginTop: '0px' }}>
          <h3 className={styles.subtitle} style={{ marginBottom: isMobile ? '16px' : '32px' }}>
            Assessment
          </h3>
          <Text size={isMobile ? 14 : 16} weight={400}>
            {program?.data.assessment_text
              ? program?.data.assessment_text
              : program?.data.school.assessment_text}
          </Text>
        </div>
      )}
      {program?.data.school.student_profile_description && (
        <div className={styles.sectionContent} style={{ marginTop: '0px' }}>
          <h3 className={styles.subtitle} style={{ marginBottom: isMobile ? '16px' : '32px' }}>
            Student profile
          </h3>
          <Text size={isMobile ? 14 : 16} weight={400}>
            {program?.data.school.student_profile_description}
          </Text>
        </div>
      )}
      <div className={styles.sectionContent} style={{ marginTop: '0px' }}>
        <div className="d-flex flex-row align-items-center gap-4">
          {program?.data.international_student_percentage && (
            <AttributeComponent
              attribute="International student rate"
              value={`${program?.data.international_student_percentage}%`}
            />
          )}
          {program?.data.men_women_percentage && (
            <AttributeComponent
              attribute="Student gender (M:F)"
              value={`${program?.data.men_women_percentage * 100} : ${
                100 - program?.data.men_women_percentage * 100
              }`}
            />
          )}
        </div>
      </div>
    </div>
  );
};
